import { Delete, FilterAlt } from "@mui/icons-material";
import {
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Select,
  TextField,
  Tooltip,
} from "@mui/material";
import DrawerCM from "components/shared/Drawer";
import generateHeadCells from "helper/generate-head-cell";
import { useCallback, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  StyledDataText,
  StyledDataTitle,
  StyledDateBox,
  StyledGrid,
  StyledGridIcons,
  StyledMenuItem,
  StyledStack,
  StyledTitle,
} from "./Financial.style";

import TableCM from "components/shared/Table";
import PaginationCM from "components/shared/Pagination";
import spliter from "helper/digit-spliter";
import useHttp from "hooks/use-http";
import {
  getEducationYearRequest,
  getStudentFinancialRequest,
  getStudentFinancialStatsRequest,
} from "api/requests";
import { useEffect } from "react";
import { PAGINATION_LIMIT } from "constants";
import DatePickerCM from "components/shared/DatePicker";
import { convertNum } from "helper/convert-num";
import LoadingCM from "components/shared/Loading";

const headCells = generateHeadCells("user_financial");

export default function Financial() {
  const [financialData, setFinancialData] = useState({
    data: [{ content: "اطلاعاتی یافت نشد" }],
    count: 0,
  });
  const [financialStats, setFinancialStats] = useState({
    paid_price: 0,
    remaining_price: 0,
    total_price: 0,
  });

  // filter states
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedPaymentStatus, setSelectedPaymentStatus] = useState();
  const [selectedPaymentType, setSelectedPaymentType] = useState("");
  const [selectedEducationalYear, setSelectedEducationalYear] = useState("");
  const [searchValue, setSearchValue] = useState("");

  const [page, setPage] = useState(1);

  const { register, control } = useForm();

  const { loading, request } = useHttp();

  // date filter handler
  const onFromDateHandler = (value) =>
    setFromDate(
      value
        .format("YYYY-MM-DD")
        .split("-")
        .map((item) => convertNum(item))
        .join("-")
    );

  const onToDateHandler = (value) =>
    setToDate(
      value
        .format("YYYY-MM-DD")
        .split("-")
        .map((item) => convertNum(item))
        .join("-")
    );

  const [educationalYears, setEducationalYears] = useState([]);
  const getEducationYear = useCallback(async () => {
    const res = await request({
      url: getEducationYearRequest,
      isMulty: true,
    });
    if (res) {
      setEducationalYears(res);
      setSelectedEducationalYear({ value: res[res.length - 2] });
    }
  }, []);

  const getFinancial = async (filter = true) => {
    const query = {
      skip: (page - 1) * PAGINATION_LIMIT,
      limit: PAGINATION_LIMIT,
      from_date: fromDate ?? null,
      to_date: toDate ?? null,
      search: searchValue ?? null,
      educational_year: selectedEducationalYear?.value ?? "*",
      payment_type: selectedPaymentType?.value ?? null,
      payment_status: selectedPaymentStatus?.value ?? null,
    };
    const res = await request({
      url: getStudentFinancialRequest,
      values: filter ? query : { skip: 0, limit: PAGINATION_LIMIT },
      isMulty: true,
    });

    const resStats = await request({
      url: getStudentFinancialStatsRequest,
    });

    if (resStats) {
      setFinancialStats({
        paid_price: convertNum(resStats.paid_price),
        remaining_price: convertNum(resStats.remaining_price),
        total_price: convertNum(resStats.total_price),
      });
    }

    if (!res.count || !res.data.length) {
      return setFinancialData({
        data: [{ content: "اطلاعاتی یافت نشد" }],
        count: 0,
      });
    }

    const transformedData = res.data.map((item) => {
      return {
        Id: item.id,
        title: item.title,
        amount: item.amount ? spliter(item.amount) : "ثبت نشده",
        payment_type:
          item.payment_type === "check" ? "پرداخت با چک" : "پرداخت نقدی",
        check_number: item?.check_number ?? "ثبت نشده",
        receipt_number: item.receipt_number ?? "ثبت نشده",
        payment_status: item.payment_status ? "پرداخت شده" : "پرداخت نشده",
        date: item?.date?.string ?? "ثبت نشده",
        payment_date: item?.payment_date?.string ?? "ثبت نشده",
      };
    });

    setFinancialData({
      data: transformedData,
      count: res.count,
    });
  };

  const getWithFilter = () => {
    setPage(1);
    getFinancial();
  };

  const getWithoutFilter = () => {
    setPage(1);
    setSelectedEducationalYear(null);
    setFromDate("");
    setToDate("");
    setSearchValue("");
    setSelectedPaymentStatus("");
    setSelectedPaymentType("");
    setSelectedEducationalYear("");
    getFinancial(false);
  };

  useEffect(() => {
    getEducationYear();
    getFinancial();
  }, [getEducationYear]);

  return (
    <DrawerCM title="امور مالی دانش آموز" $styledHeader>
      {loading ? (
        <LoadingCM />
      ) : (
        <>
          <StyledStack>
            <StyledTitle>امار کلی (ریال) </StyledTitle>
            <Grid container>
              <StyledGrid item md={4} xs={12}>
                <StyledDataTitle>کل مبلغ قابل پرداخت</StyledDataTitle>
                <StyledDataText>
                  {spliter(financialStats.total_price)}
                </StyledDataText>
              </StyledGrid>
              <StyledGrid item md={4} xs={12}>
                <StyledDataTitle>مقدار پرداخت شده</StyledDataTitle>
                <StyledDataText>
                  {spliter(financialStats.paid_price)}
                </StyledDataText>
              </StyledGrid>
              <StyledGrid item md={4} xs={12}>
                <StyledDataTitle> مبلغ باقی مانده</StyledDataTitle>
                <StyledDataText>
                  {spliter(financialStats.remaining_price)}
                </StyledDataText>
              </StyledGrid>
            </Grid>
          </StyledStack>
          <StyledStack>
            <Grid container>
              <Grid item sm={4} xs={12} p={0.5}>
                <TextField
                  label="جست و جو"
                  fullWidth
                  id="outlined-size-small"
                  size="small"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                />
              </Grid>
              <Grid item sm={4} xs={12} p={0.5}>
                <Controller
                  control={control}
                  defaultValue={""}
                  name="select-educational-year"
                  render={() => (
                    <FormControl fullWidth size="small">
                      <InputLabel id="select-educational-year">
                        انتخاب سال تحصیلی
                      </InputLabel>
                      <Select
                        id="educationalYear"
                        name="educationalYear"
                        label="انتخاب سال تحصیلی"
                        labelId="select-educational-year"
                        value={selectedEducationalYear?.value ?? ""}
                        {...register("educationalYear")}
                        onChange={(e) =>
                          setSelectedEducationalYear({
                            value: e.target.value,
                          })
                        }
                      >
                        <StyledMenuItem value={"*"}>{"همه"}</StyledMenuItem>
                        {educationalYears?.map((item, index) => {
                          return (
                            <StyledMenuItem key={index} value={item}>
                              {item + 1} - {item}
                            </StyledMenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item sm={4} xs={12} p={0.5}>
                <Controller
                  control={control}
                  defaultValue={""}
                  name="select-payment-type"
                  render={() => (
                    <FormControl fullWidth size="small">
                      <InputLabel id="student_class">
                        انتخاب روش پرداخت
                      </InputLabel>
                      <Select
                        id="payment_status"
                        name="payment_status"
                        label="انتخاب روش پرداخت"
                        labelId="select-payment_status"
                        value={selectedPaymentType?.value || ""}
                        {...register("payment_status")}
                        onChange={(e) =>
                          setSelectedPaymentType({
                            value: e.target.value,
                          })
                        }
                      >
                        <StyledMenuItem value={"*"}>{"همه"}</StyledMenuItem>
                        <StyledMenuItem value={"check"}>
                          پرداخت با چک
                        </StyledMenuItem>
                        <StyledMenuItem value={"money"}>
                          پرداخت نقدی
                        </StyledMenuItem>
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item sm={4} xs={12} p={0.5}>
                <Controller
                  control={control}
                  defaultValue={""}
                  name="payment_status"
                  render={() => (
                    <FormControl fullWidth size="small">
                      <InputLabel id="payment_status">وضعیت پرداخت</InputLabel>
                      <Select
                        id="payment_status"
                        name="payment_status"
                        label="وضعیت پرداخت"
                        labelId="select-payment_status"
                        value={selectedPaymentStatus?.value || ""}
                        {...register("payment_status")}
                        onChange={(e) =>
                          setSelectedPaymentStatus({
                            value: e.target.value,
                          })
                        }
                      >
                        <StyledMenuItem value={"*"}>{"همه"}</StyledMenuItem>
                        <StyledMenuItem value={"1"}>پرداخت شده</StyledMenuItem>
                        <StyledMenuItem value={"0"}>پرداخت نشده</StyledMenuItem>
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item sm={4} xs={12} py={0.5} px={0.5}>
                <StyledDateBox>
                  <DatePickerCM
                    onChange={onFromDateHandler}
                    render={(value, openCalendar) => {
                      return (
                        <TextField
                          label="از تاریخ"
                          variant="outlined"
                          fullWidth
                          size="small"
                          id="from_created_at"
                          {...register("from_created_at")}
                          value={fromDate ? fromDate.split("-").join("/") : ""}
                          onClick={openCalendar}
                        />
                      );
                    }}
                  />
                </StyledDateBox>
              </Grid>
              <Grid item sm={4} xs={12} p={0.5}>
                <StyledDateBox>
                  <DatePickerCM
                    onChange={onToDateHandler}
                    render={(value, openCalendar) => {
                      return (
                        <TextField
                          label="تا تاریخ"
                          variant="outlined"
                          fullWidth
                          size="small"
                          id="to_created_at"
                          {...register("to_created_at")}
                          value={toDate ? toDate.split("-").join("/") : ""}
                          onClick={openCalendar}
                        />
                      );
                    }}
                  />
                </StyledDateBox>
              </Grid>
              <StyledGridIcons item sm={12} xs={12} p={0.5} mt={2}>
                <IconButton color="primary" onClick={getWithFilter}>
                  <Tooltip title="اعمال فیلتر" placement="top">
                    <FilterAlt />
                  </Tooltip>
                </IconButton>
                <IconButton color="error" onClick={getWithoutFilter}>
                  <Tooltip title="حذف فیلتر" placement="top">
                    <Delete />
                  </Tooltip>
                </IconButton>
              </StyledGridIcons>
            </Grid>
          </StyledStack>
          <Grid container>
            <Grid item xs={12} my={2}>
              <TableCM
                rows={financialData.data}
                page={page}
                headCells={headCells}
                colspan={financialData.count === 0 ? "10" : ""}
                fontSize="12px"
                cellFontSize="12px"
              />
              {financialData.data.length < 1 ? (
                ""
              ) : (
                <PaginationCM
                  handleChange={(_, val) => setPage(val)}
                  page={page}
                  count={financialData.count}
                />
              )}
            </Grid>
          </Grid>
        </>
      )}
    </DrawerCM>
  );
}
